import { ReactNode } from 'react';
import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * StatCard — small widget used on the dashboard.
 *
 * Renders an icon + label + big number + optional footer/suffix.
 * Theming follows the Hot Wheels palette (red accent on dark).
 */

interface StatCardProps {
  icon: LucideIcon;
  label: string;
  value: string | number;
  suffix?: string;
  footer?: ReactNode;
  className?: string;
  accent?: 'red' | 'gold' | 'blue' | 'green';
}

const accentMap = {
  red: 'from-blue-500/20 to-blue-500/5 text-blue-500',
  gold: 'from-yellow-500/20 to-yellow-500/5 text-yellow-500',
  blue: 'from-blue-500/20 to-blue-500/5 text-blue-500',
  green: 'from-emerald-500/20 to-emerald-500/5 text-emerald-500',
};

export function StatCard({
  icon: Icon,
  label,
  value,
  suffix,
  footer,
  className,
  accent = 'red',
}: StatCardProps) {
  return (
    <div
      className={cn(
        'rounded-2xl border border-zinc-800 bg-zinc-900 p-5 flex flex-col gap-3 hover:border-zinc-700 transition-colors',
        className
      )}
    >
      <div className="flex items-center justify-between">
        <div className={cn('rounded-xl bg-gradient-to-br p-2.5', accentMap[accent])}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <div>
        <div className="text-xs text-zinc-400 uppercase tracking-wider">{label}</div>
        <div className="text-3xl font-bold text-white mt-1">
          {value}
          {suffix && <span className="text-base text-zinc-400 ml-1">{suffix}</span>}
        </div>
      </div>
      {footer && <div className="text-xs text-zinc-500">{footer}</div>}
    </div>
  );
}